// SAM-1096 — Featured carousel Preview (/settings/ads/preview), mock C6: every ad in the app today, in swipe order,
// at the size residents get, and what is not shown (and why).
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAds } from '../../hooks/useAds';
import type { Ad } from '../../hooks/useAds';
import { useT } from '../../i18n/admin';
import { CmsStateBadge } from '../../components/cms/CmsStateBadge';
import { AdCard, FeaturedPreview, PhoneFrame } from '../../components/cms/AppPreviews';
import { Segmented, PageHead, btnGhost } from '../../components/cms/EditorParts';
import { adHint, cmsState, dayOf, todayInBusinessClock } from '../../utils/cmsState';

export const AdsPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { fmt } = useT();
  const { ads, isLoading } = useAds();
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const startAd = (useLocation().state as { adId?: number } | null)?.adId;
  const [picked, setPicked] = useState<number | null>(null);
  const today = todayInBusinessClock();

  const { live, off } = useMemo(() => {
    const rows = [...ads]
      .sort((a, b) => (a.priority || 9e9) - (b.priority || 9e9) || (a.id ?? 0) - (b.id ?? 0))
      .map((ad) => ({ ad, state: cmsState(ad, today) }));
    return { live: rows.filter((r) => r.state === 'published'), off: rows.filter((r) => r.state !== 'published') };
  }, [ads, today]);

  // Opened from an ad's eye: start on that ad's slide when it is in the app.
  const active = picked ?? Math.max(0, live.findIndex((r) => r.ad.id === startAd));
  const setActive = setPicked;

  const card = (ad: Ad) => ({
    title: (lang === 'en' ? ad.title_en : ad.title_es) || ad.title_en,
    text: (lang === 'en' ? ad.short_description_en : ad.short_description_es) || '',
    color: ad.background_color,
    imageKey: ad.art_img_url,
  });
  const range = (ad: Ad) => [fmt.date(dayOf(ad.publish_date)), fmt.date(dayOf(ad.expiration_date))].filter(Boolean).join(' – ');
  const offSummary = [
    off.filter((r) => r.state === 'expired').length && `${off.filter((r) => r.state === 'expired').length} expired`,
    off.filter((r) => r.state === 'draft').length && `${off.filter((r) => r.state === 'draft').length} drafts`,
    off.filter((r) => r.state === 'scheduled').length && `${off.filter((r) => r.state === 'scheduled').length} scheduled`,
  ].filter(Boolean).join(', ');

  if (isLoading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="flex h-full flex-col bg-gray-50 text-gray-900">
      <PageHead
        back="Ads"
        onBack={() => navigate('/settings/ads')}
        title="Preview · Featured carousel"
        aside={<span className="text-[13px] text-gray-500">{live.length} ads in the app today, {fmt.date(today)}</span>}
        actions={
          <>
            <Segmented label="Language" value={lang} options={[{ id: 'en', text: 'English' }, { id: 'es', text: 'Español' }]} onChange={(v) => setLang(v as 'en' | 'es')} />
            <button type="button" className={btnGhost} onClick={() => navigate('/settings/ads')}>Edit order</button>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex min-h-full gap-10 px-8 pb-12 pt-6">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div>
            <h2 className="m-0 text-base font-semibold">Swipe order</h2>
            <p className="m-0 text-[13px] text-gray-500">Each card at the size residents get. Slide 1 is what they see first.</p>
          </div>
          {live.length === 0 && <p className="text-sm text-gray-500">No ads are in the app today. The Featured section hides itself.</p>}
          {live.map(({ ad }, i) => (
            <button
              key={ad.id}
              type="button"
              aria-pressed={active === i}
              onClick={() => setActive(i)}
              className="flex items-center gap-4 border-0 bg-transparent p-0 text-left"
            >
              <span className="w-6 text-lg font-bold text-gray-900">{i + 1}</span>
              <span className="flex flex-col gap-1.5">
                <AdCard ad={card(ad)} />
                <span className="text-xs text-gray-500">{range(ad)}{adHint(ad, today) && ` · ${adHint(ad, today)}`}</span>
              </span>
            </button>
          ))}
          {off.length > 0 && (
            <div className="mt-2">
              <h2 className="m-0 text-base font-semibold">Not shown</h2>
              <p className="m-0 mb-2 text-[13px] text-gray-500">{off.length} ads: {offSummary}</p>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {off.map(({ ad, state }) => (
                  <li key={ad.id} className="flex items-center gap-3 text-sm">
                    <span className="min-w-0 flex-1 truncate">{ad.title_en}</span>
                    <CmsStateBadge state={state} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-center gap-3.5">
          <PhoneFrame height={560} label="Home · Featured">
            <FeaturedPreview ads={live.map(({ ad }) => card(ad))} active={Math.min(active, Math.max(0, live.length - 1))} />
          </PhoneFrame>
          <div className="text-center">
            <div className="text-sm font-bold">Home · Featured</div>
            <div className="text-xs text-gray-500">Slide {Math.min(active, Math.max(0, live.length - 1)) + 1} of {live.length}</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
